/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import moment from 'moment'
import { calendarTheme } from './calendar_theme'

const TIMED_EVENT_GAP = 6

export const NO_TITLE_FALLBACK = '(No title)'

export const getOccurrenceDurationMinutes = (occurrence) => {
  if (!occurrence?.start || !occurrence?.end) {
    return 0
  }

  return occurrence.end.diff(occurrence.start, 'minutes')
}

export const formatCompactSlotLabel = (occurrence) => {
  const duration = getOccurrenceDurationMinutes(occurrence)
  if (duration <= calendarTheme.halfHourDurationMinutes) {
    return occurrence?.start ? occurrence.start.format('hh:mm A') : ''
  }

  return occurrence?.slotLabel || ''
}

export const formatSlotLabel = (slot) => {
  if (slot?.all_day) {
    return 'All day'
  }

  const startTime = slot?.start_time ? moment(slot.start_time, 'HH:mm').format('hh:mm A') : ''
  const endTime = slot?.end_time ? moment(slot.end_time, 'HH:mm').format('hh:mm A') : ''

  if (!startTime || !endTime) {
    return 'Custom time'
  }

  return `${startTime} - ${endTime}`
}

export const formatWeekLabel = (weekStart) => {
  const weekEnd = weekStart.clone().add(6, 'days')

  if (weekStart.isSame(weekEnd, 'month')) {
    return `${weekStart.format('MMM D')}-${weekEnd.format('D, YYYY')}`
  }

  return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`
}

export const formatMonthLabel = (monthStart) => monthStart.format('MMMM YYYY')

export const formatYearLabel = (yearStart) => yearStart.format('YYYY')

export const getMonthGridDays = (monthStart) => {
  const gridStart = monthStart.clone().startOf('month').startOf('week')
  const gridEnd = monthStart.clone().endOf('month').endOf('week')
  const days = []
  const cursor = gridStart.clone()

  while (cursor.isSameOrBefore(gridEnd, 'day')) {
    days.push(cursor.clone())
    cursor.add(1, 'day')
  }

  return days
}

export const groupOccurrencesByDate = (occurrences) => {
  const grouped = {}

  ;(occurrences || []).forEach((occurrence) => {
    if (!grouped[occurrence.date]) {
      grouped[occurrence.date] = []
    }

    grouped[occurrence.date].push(occurrence)
  })

  Object.keys(grouped).forEach((key) => {
    grouped[key].sort((left, right) => left.start.valueOf() - right.start.valueOf())
  })

  return grouped
}

export const buildCardsLookup = (cardGroups) => {
  return (cardGroups || []).reduce((accumulator, group) => {
    accumulator[group?.restaurant_group_id] = (group?.cards || []).reduce((groupAccumulator, card) => {
      groupAccumulator[card?.id] = card
      return groupAccumulator
    }, {})

    return accumulator
  }, {})
}

export const getSlotCards = (event, slot, cardsLookup) => {
  const scopedCards = cardsLookup?.[event?.restaurant_group_id] || {}

  return (slot?.cards || []).map((slotCard) => {
    const cardDetails = scopedCards?.[slotCard?.id] || {}

    return {
      id: slotCard?.id,
      name: cardDetails?.name || `Card #${slotCard?.id}`,
      color: cardDetails?.color || calendarTheme.defaultCardColor,
      blocked: Boolean(slotCard?.blocked),
    }
  })
}

export const buildOccurrences = (events, cardsLookup) => {
  return (events || []).flatMap((event) => {
    const slotRows = event?.slots?.length
      ? event.slots
      : [{
          id: `event-${event?.id}-no-slot`,
          placeholder: true,
          all_day: true,
          cards: [],
        }]

    return slotRows.flatMap((slot) => {
      const slotLabel = slot?.placeholder ? 'No time slots' : formatSlotLabel(slot)
      const cards = getSlotCards(event, slot, cardsLookup)
      const blockedCards = cards.filter((card) => card.blocked)
      const eventStart = moment(event?.start_date)
      const eventEnd = moment(event?.end_date)

      if (!eventStart.isValid() || !eventEnd.isValid()) {
        return []
      }

      const occurrences = []
      const currentDay = eventStart.clone()

      while (currentDay.isSameOrBefore(eventEnd, 'day')) {
        let start = slot?.all_day
          ? currentDay.clone().startOf('day')
          : moment(`${currentDay.format('YYYY-MM-DD')} ${slot?.start_time || '00:00'}`, 'YYYY-MM-DD HH:mm', true)
        let end = slot?.all_day
          ? currentDay.clone().endOf('day')
          : moment(`${currentDay.format('YYYY-MM-DD')} ${slot?.end_time || '23:59'}`, 'YYYY-MM-DD HH:mm', true)

        if (!start.isValid()) {
          start = currentDay.clone().startOf('day')
        }

        if (!end.isValid() || !end.isAfter(start)) {
          end = start.clone().add(calendarTheme.halfHourDurationMinutes, 'minutes')
        }

        occurrences.push({
          id: `${event?.id}-${slot?.id}-${currentDay.format('YYYY-MM-DD')}`,
          event,
          slot,
          title: event?.name,
          slotLabel,
          date: currentDay.format('YYYY-MM-DD'),
          start,
          end,
          cards,
          blockedCards,
        })

        currentDay.add(1, 'day')
      }

      return occurrences
    })
  })
}

const finalizeTimedGroupLayouts = (group, layouts, startHour, hourRowHeight) => {
  if (!group.length) {
    return
  }

  const laneEndTimes = []
  const assignments = []

  group.forEach((occurrence) => {
    const occurrenceStart = occurrence.start.valueOf()
    let laneIndex = laneEndTimes.findIndex((laneEndTime) => laneEndTime <= occurrenceStart)

    if (laneIndex === -1) {
      laneIndex = laneEndTimes.length
    }

    laneEndTimes[laneIndex] = occurrence.end.valueOf()
    assignments.push({ occurrence, laneIndex })
  })

  const laneCount = Math.max(1, laneEndTimes.length)

  assignments.forEach(({ occurrence, laneIndex }) => {
    const startValue = occurrence.start.hours() + occurrence.start.minutes() / 60
    const endValue = occurrence.end.hours() + occurrence.end.minutes() / 60
    const boundedStart = Math.max(startHour, startValue)
    const boundedEnd = Math.max(boundedStart + 0.5, Math.min(calendarTheme.endHour + 1, endValue))
    const top = (boundedStart - startHour) * hourRowHeight
    // Keep compact events above the 24x24 AA touch target floor; we intentionally do not target the 44x44 AAA size here to preserve calendar density.
    const height = Math.max(calendarTheme.minEventPxHeight, (boundedEnd - boundedStart) * hourRowHeight)
    const eventInset = 8

    if (laneCount === 1) {
      layouts[occurrence.id] = {
        top,
        height,
        left: `${eventInset}px`,
        width: `calc(100% - ${eventInset * 2}px)`,
        laneCount,
      }
      return
    }

    const totalGap = (laneCount - 1) * TIMED_EVENT_GAP
    layouts[occurrence.id] = {
      top,
      height,
      left: `calc(${eventInset}px + ${laneIndex} * ((100% - ${eventInset * 2}px - ${totalGap}px) / ${laneCount} + ${TIMED_EVENT_GAP}px))`,
      width: `calc((100% - ${eventInset * 2}px - ${totalGap}px) / ${laneCount})`,
      laneCount,
    }
  })
}

export const buildDayOccurrenceLayouts = ({ occurrences, calendarHeight, startHour, hourRowHeight }) => {
  const layouts = {}
  const allDayOccurrences = []
  const timedOccurrences = []

  ;(occurrences || []).forEach((occurrence) => {
    if (occurrence?.slot?.all_day || occurrence?.slot?.placeholder) {
      allDayOccurrences.push(occurrence)
      return
    }

    timedOccurrences.push(occurrence)
  })

  allDayOccurrences.forEach((occurrence, index) => {
    layouts[occurrence.id] = {
      top: 4,
      height: calendarHeight - 8,
      left: `${8 + index * 8}px`,
      width: `calc(100% - ${16 + index * 8}px)`,
      laneCount: 1,
    }
  })

  const sortedTimedOccurrences = [...timedOccurrences].sort((left, right) => left.start.valueOf() - right.start.valueOf())
  let currentGroup = []
  let currentGroupEnd = null

  sortedTimedOccurrences.forEach((occurrence) => {
    const occurrenceStart = occurrence.start.valueOf()
    const occurrenceEnd = occurrence.end.valueOf()

    if (!currentGroup.length || occurrenceStart < currentGroupEnd) {
      currentGroup.push(occurrence)
      currentGroupEnd = Math.max(currentGroupEnd || occurrenceEnd, occurrenceEnd)
      return
    }

    finalizeTimedGroupLayouts(currentGroup, layouts, startHour, hourRowHeight)
    currentGroup = [occurrence]
    currentGroupEnd = occurrenceEnd
  })

  finalizeTimedGroupLayouts(currentGroup, layouts, startHour, hourRowHeight)

  return layouts
}
